import { TextField } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import type { FormField } from '../interfaces';
import { useGetProsjektByggListQuery } from '../services/rtkApi';
import { Loader } from '@klp/design/Loader';
import { useSelector } from 'react-redux';
import { Autocomplete } from '@material-ui/lab';
import {
    getLocalizedLabel,
    handleBuildingOptionByValue,
    isValidBuildingJson,
} from '../utils';
import { contextSelectors } from '@klp/contextSelector';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';
import { useFormContext } from '../context/FormContext';
import { isValidFormRegion } from '../utils/validation/validators';

export const EiendomSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
}) => {
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField<string | undefined>(field.id);
    const { setFieldValue } = useFormikContext();
    const { selectedContext } = useSelector(contextSelectors);
    const { data, isLoading, isFetching } =
        useGetProsjektByggListQuery(undefined);
    const { setRegionContext } = useFormContext();

    const saveRegionToContext = (selectedRegion: string | undefined) => {
        if (selectedRegion && isValidFormRegion(selectedRegion)) {
            setRegionContext(selectedRegion);
        } else {
            setRegionContext(undefined);
        }
    };

    useEffect(() => {
        if (!selectedContext) return;

        const eiendomFromContext = JSON.stringify({
            id: selectedContext.id,
            navn: selectedContext.navn,
        });

        if (!isValidBuildingJson(formikField.value)) {
            setFieldValue(field.id, eiendomFromContext);
            saveRegionToContext(selectedContext.region);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContext]);

    const selectedValue = handleBuildingOptionByValue(formikField?.value, data);

    const handleChange = (value: Bygg | null) => {
        const fieldValue = value
            ? JSON.stringify({
                  id: value.id,
                  navn: value.navn,
              })
            : null;
        setFieldValue(field.id, fieldValue);
        saveRegionToContext(value?.region);
    };

    return (
        <Loader isLoading={isLoading || isFetching}>
            <Autocomplete<Bygg>
                id="eiendomsnavn"
                selectOnFocus={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                disabled={isReadOnly}
                options={data || []}
                getOptionLabel={(option) => option.navn ?? ''}
                getOptionSelected={(option, value) => option.id === value?.id}
                value={selectedValue ?? null}
                onChange={(_event, newValue) => handleChange(newValue)}
                loading={isLoading || isFetching}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        required={isRequired}
                        variant="outlined"
                        disabled={!!isReadOnly}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        label={getLocalizedLabel(
                            `project.formFields.${field?.id}`,
                            field?.label,
                            field?.id,
                        )}
                        helperText={meta.touched ? meta.error : ''}
                        error={meta.touched && Boolean(meta.error)}
                    />
                )}
            />
        </Loader>
    );
};
